import type { SuggestionOptions } from '@tiptap/suggestion'
import { apiV3 } from '@opallabs/api'
import type { MentionableResource } from '@opallabs/text-editor/src/Thread/MentionList'

const SUGGESTION_LIMIT = 6
const DEBOUNCE_MS = 200

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Suggestion source for the feedback composer: active users matching what has
 * been typed after the `@`. Users only, matching what legacy annotation comments
 * can mention. Mentions of users outside the content's workspace are dropped by
 * the server when it decides who to notify, so this doesn't scope the search.
 */
export const userMentionItems =
  (): SuggestionOptions<MentionableResource>['items'] => {
    let latestRequest = 0

    return async ({ query }) => {
      const term = query.trim()
      if (term.length === 0) return []

      const request = ++latestRequest
      await delay(DEBOUNCE_MS)
      if (request !== latestRequest) return []

      const users = await apiV3.users.readCollection({
        query: {
          filter: { name: term, is_active: true },
          page: { limit: SUGGESTION_LIMIT },
        },
      })
      if (request !== latestRequest) return []

      return users.data.map((user) => ({
        // The markup has to carry the numeric id: both the fan-out's user lookup
        // and the feed's `auto_link_mentions` resolve mentions by it, and a uuid
        // silently renders as unlinked bold text.
        id: user.attributes.legacy_id,
        label: user.attributes.full_name,
        resource_type: 'user' as const,
        // A document comment anchors its mentions to the resource being commented
        // on; a pin's anchor is the annotation, so there is nothing to name.
        document_resource_type: '',
        document_resource_id: '',
      }))
    }
  }
